import { Stack, Typography, useMediaQuery } from '@mui/material'
import { FunctionComponent } from 'react'
import gaiaTheme from '../../../root-components/theme'
import { usePdfExportMode } from '../../../hooks/use-pdf-export-mode'
import { GraphBarChartStacked } from '../../../components/presentation-components/analytics-components/graphs/graph-bar-chart-stacked'
import { buildGenericSeriesStack, valueFormatter } from '../../../shared/interfaces/analytics'
import {
  getGaiaReportSectionShellSx,
  getGaiaReportBodyTextSx,
} from '../../public-reports/components/gaia-report-web-page-pdf-styles'
import type { IColorPalatte } from '../shared-services/project-reports.model'
import { useReportContentOverride } from '../../public-reports/shared-services/gaia-report-content'

function normaliseStackedData(raw: any): any[] {
  if (!raw) return []
  if (Array.isArray(raw)) return raw
  return raw.breakdown ?? raw.marketBased ?? raw.locationBased ?? []
}

export const ProjectReportWebPageTimeline: FunctionComponent<{
  colorPalatte: IColorPalatte
  timelineData: any
  projectLabel: string
}> = ({ colorPalatte, timelineData, projectLabel }) => {
  const isMobileModeMd = useMediaQuery(gaiaTheme.breakpoints.down('md'))
  const isMobileModeSm = useMediaQuery(gaiaTheme.breakpoints.down('sm'))
  const pdfExport = usePdfExportMode()

  const stackedItems = normaliseStackedData(timelineData)
  const series = buildGenericSeriesStack(stackedItems, valueFormatter)

  const heading = useReportContentOverride('projectEmissionsOverTime.heading')
  const body = useReportContentOverride('projectEmissionsOverTime.body')

  return (
    <Stack sx={{
      ...getGaiaReportSectionShellSx({ backgroundColor: colorPalatte.veryDark, pdfExport }),
      ...(pdfExport ? {} : { mt: -5, position: 'relative', zIndex: 4 }),
    }}>
      <Typography variant={isMobileModeSm ? 'h5' : 'h4'} sx={{ color: 'white', fontWeight: 700, mb: 2 }}>
        {heading ?? 'Emissions Over Time'}
      </Typography>
      <Typography sx={{ ...getGaiaReportBodyTextSx({ color: 'rgba(255,255,255,0.8)', isMobileModeMd, isMobileModeSm, pdfExport }), mb: 2 }}>
        {body ?? `Monthly emissions for this ${projectLabel.toLowerCase()} across its full duration.`}
      </Typography>
      {stackedItems.length > 0 ? (
        <div className='chart-block'>
          <GraphBarChartStacked
            title='Monthly Emissions'
            dataset={stackedItems}
            series={series}
            colors={colorPalatte.chartSeries ?? [colorPalatte.main, colorPalatte.light, colorPalatte.veryLight]}
            backgroundColor='transparent'
            chartHeight={pdfExport ? 260 : 400}
            paperPadding={pdfExport ? 2 : 3}
            paperHeight={pdfExport ? 320 : undefined}
          />
        </div>
      ) : (
        <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontStyle: 'italic', mt: 2 }}>
          No monthly data available.
        </Typography>
      )}
    </Stack>
  )
}
